// How the exam's staff pieces (components/ExamStaff, components/ExamReview)
// speak and link where they are shown (RFC-062 model 3): on Share's exam
// page (/ea/{id}) in Share's words, its hand-ins graded on the instructor's
// machine by byj; in an edition's console in the edition's words, graded by
// the exam service itself, a sitting watched on the console's own page and
// nothing leading to Share's pages.
import { createContext, useContext } from "react";
import type { ExamAttempt } from "./api";
import { t } from "./i18n";

export type StaffTab = "submissions" | "review" | "sitting" | "audit";

export type Staff = {
  /** Whoever hands in: a table's column head. */
  person: () => string;
  /** A grade released to them. */
  release: () => string;
  /** The totals' head in review. */
  totals: () => string;
  /** Review with no reviewer named. */
  noReviewers: () => string;
  /** Hand-ins graded on the instructor's machine (byj's commands shown), or by the service. */
  gradedHere: boolean;
  /** The assignment's bar links its class (Share's class page). */
  classLink: boolean;
  /** A grade links its recording (Share's /r/{id}). */
  recordings: boolean;
  /** Where a live sitting is watched; none, not linked. */
  watch: (a: ExamAttempt) => string | undefined;
  /** The instructor's view's tabs, in order. */
  tabs: StaffTab[];
  /** A question by its id, as its author knows it; else by its number on the paper. */
  questionIds: boolean;
  /** Who can be named a reviewer: one who has signed in where this page is. */
  reviewerHint: () => string;
};

/** Share's own exam page. */
export const shareStaff: Staff = {
  person: () => t("exams:colStudent"),
  release: () => t("exams:gradeRelease"),
  totals: () => t("exams:reviewTotals"),
  noReviewers: () => t("exams:noReviewers"),
  gradedHere: true,
  classLink: true,
  recordings: true,
  watch: (a) => (a.share_id ? `/s/${encodeURIComponent(a.share_id)}` : undefined),
  tabs: ["submissions", "review", "sitting", "audit"],
  questionIds: true,
  reviewerHint: () => t("exams:reviewerHint"),
};

export const StaffFor = createContext<Staff>(shareStaff);

export const useStaff = () => useContext(StaffFor);

/** A question as the staff name it: by its id on Share's page, by its number on the paper in a console (byj-1wv.118.14). */
export function useQuestionName(questions: readonly string[]) {
  const staff = useStaff();
  return (id: string | undefined) => {
    const n = id === undefined ? -1 : questions.indexOf(id);
    return staff.questionIds || n < 0 ? (id ?? "") : t("room:questionN", { n: n + 1 });
  };
}
